import type { OutreachBatch } from '@secretario/shared';
import { OUTREACH_WA_GAP_SECONDS } from '@secretario/shared';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'wouter';
import { hapticTap } from '../../lib/native.js';
import {
  clock,
  firstName,
  focusOf,
  isRunning,
  outreachError,
  useOpenNext,
  useOutreachList,
  useNativeOutreachNotice,
  useSecondsUntil,
} from '../../lib/outreach.js';
import { isNativeShell } from '../../lib/platform.js';
import { OutreachRing } from './OutreachRing.js';
import { WhatsAppTurn } from './WhatsAppTurn.js';
import './outreach.css';

/**
 * O envio minimizado (estudo aprovado em 26/10): uma cápsula acima da barra de baixo em toda
 * tela do celular e um cartão no canto da mesa, com o anel da contagem e o andamento. Pronta a
 * próxima, fica verde, vibra uma vez e abre o WhatsApp direto. O título da aba conta junto.
 * Some na página do próprio envio e na tela da TV; arrastar para o lado esconde até a próxima.
 */
const COLLAPSE_KEY = 'sec.envio.cartao';
const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
};
const useWide = () => {
  const query = '(min-width: 1024px)';
  const [wide, setWide] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const on = () => setWide(media.matches);
    media.addEventListener('change', on);
    return () => media.removeEventListener('change', on);
  }, []);
  return wide;
};

function sentOf(batch: OutreachBatch) {
  const reach = batch.counts.app + batch.counts.whatsapp;
  return { sent: batch.counts.appSent + batch.counts.whatsappSent, reach };
}

export function OutreachCapsule({ enabled }: { enabled: boolean }) {
  const [location] = useLocation();
  const listQ = useOutreachList(enabled);
  const batches = listQ.data ?? [];
  const focus = focusOf(batches);
  const wide = useWide();
  const [sheet, setSheet] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [finished, setFinished] = useState<OutreachBatch | null>(null);
  const [error, setError] = useState<string | null>(null);
  const openNext = useOpenNext();

  // Terminou um que estava andando: "Envio concluído" por 10 s.
  const running = useRef(new Set<string>());
  useEffect(() => {
    const now = new Set(batches.filter(isRunning).map((batch) => batch.id));
    const ended = batches.find(
      (batch) => running.current.has(batch.id) && !now.has(batch.id) && batch.state === 'concluido',
    );
    running.current = now;
    if (!ended) return;
    setFinished(ended);
    const timer = window.setTimeout(() => setFinished(null), 10_000);
    return () => window.clearTimeout(timer);
  }, [batches]);

  const batch = focus?.batch ?? null;
  const next = batch?.next ?? null;
  const waiting = useSecondsUntil(next?.hold === 'espera' ? next.readyAt : null);
  const ready =
    Boolean(batch && batch.state === 'enviando' && next) &&
    next?.hold !== 'teto_do_dia' &&
    waiting === 0;
  const key = batch ? `${batch.id}:${ready ? next!.itemId : 'espera'}` : '';
  // No Android, o aviso fixo acompanha o mesmo envio, mesmo com a cápsula escondida.
  useNativeOutreachNotice(enabled ? batch : null);

  // Na volta do WhatsApp (o app ou a aba de novo à vista): a pergunta pelo anterior.
  const asked = useRef<string | null>(null);
  useEffect(() => {
    const open = batch?.open;
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !open || asked.current === open.itemId) return;
      asked.current = open.itemId;
      if (!wide) setSheet(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [batch?.open, wide]);

  // O título da aba conta junto (só no navegador; no app quem avisa é o aviso do Android).
  const title = !batch
    ? null
    : batch.state === 'pausado'
      ? 'Pausado · Envio'
      : ready
        ? `Pronta: ${firstName(next!.name)}`
        : next && waiting > 0
          ? `${clock(waiting)} · Envio`
          : `${sentOf(batch).sent} de ${sentOf(batch).reach} · Envio`;
  const base = useRef<string | null>(null);
  useEffect(() => {
    if (isNativeShell()) return;
    if (title) {
      base.current ??= document.title;
      document.title = title;
    } else if (base.current !== null) {
      document.title = base.current;
      base.current = null;
    }
  }, [title]);

  // Fica logo acima da barra de baixo (quando ela existe nesta tela).
  useEffect(() => {
    const place = () => {
      const nav = document.querySelector<HTMLElement>('.psn-nav');
      // A barra é fixa (sem offsetParent): conta a altura quando ela está à vista.
      const box = nav?.getBoundingClientRect();
      const shown = nav && box && box.height > 0 && getComputedStyle(nav).display !== 'none';
      const height = shown ? box.height : 0;
      document.documentElement.style.setProperty(
        '--oc-bottom',
        `${Math.round(height) + (height ? 26 : 12)}px`,
      );
    };
    place();
    const timer = window.setTimeout(place, 400);
    window.addEventListener('resize', place);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', place);
    };
  }, [location, batch?.id]);

  // Arrastar para o lado esconde até a próxima ficar pronta.
  const drag = useRef<{ x: number; id: number } | null>(null);
  const [dx, setDx] = useState(0);

  const hidden =
    !enabled ||
    location.startsWith('/tela') ||
    location.startsWith('/controle') ||
    (batch && location === `/envios/${batch.id}`) ||
    (batch && dismissed === key);

  // Com a cápsula ou o cartão à vista, o fim da página ganha espaço (nada fica escondido).
  const shown = Boolean(batch && !hidden);
  useEffect(() => {
    document.documentElement.classList.toggle('oc-on', shown);
    return () => document.documentElement.classList.remove('oc-on');
  }, [shown]);

  if (finished && !batch && !location.startsWith('/envios/')) {
    const { sent } = sentOf(finished);
    return createPortal(
      <Link href={`/envios/${finished.id}`} className={`oc oc-pill done${wide ? ' wide' : ''}`}>
        <span className="oc-check" aria-hidden="true">
          ✓
        </span>
        <span>
          Envio concluído · {sent} {sent === 1 ? 'enviado' : 'enviados'}
        </span>
      </Link>,
      document.body,
    );
  }
  if (!batch || hidden) return null;

  const { sent, reach } = sentOf(batch);
  const go = () => {
    hapticTap();
    setError(null);
    void openNext(batch.id).catch((failure: unknown) => {
      setError(outreachError(failure));
      setSheet(true);
    });
  };
  const label =
    batch.state === 'pausado' ? (
      <>
        <span className="oc-pause" aria-hidden="true" />
        <span>
          Pausado · {sent} de {reach} · <u>Retomar</u>
        </span>
      </>
    ) : ready ? (
      <span>Abrir WhatsApp de {firstName(next!.name)} ›</span>
    ) : next ? (
      <>
        <OutreachRing value={waiting / OUTREACH_WA_GAP_SECONDS} size={26} tone="wa" />
        <span>
          {next.hold === 'teto_do_dia'
            ? `${firstName(next.name)} amanhã`
            : `${firstName(next.name)} em ${clock(waiting)}`}
          <small>
            {' '}
            · {sent} de {reach}
          </small>
        </span>
      </>
    ) : batch.open ? (
      <span>Mandou para {firstName(batch.open.name)}?</span>
    ) : (
      <>
        <OutreachRing value={reach ? sent / reach : 0} size={26} />
        <span>
          Pelo app · {sent.toLocaleString('pt-BR')} de {reach.toLocaleString('pt-BR')}
        </span>
      </>
    );
  const more = focus && focus.more > 0 ? <small className="oc-more">+{focus.more}</small> : null;

  if (wide && !collapsed)
    return createPortal(
      <aside className="oc-card" aria-label={`Envio: ${batch.title}`}>
        <header>
          <b>{batch.title}</b>
          {more}
          <Link href={`/envios/${batch.id}`} className="oc-link">
            Abrir
          </Link>
          <button
            type="button"
            className="oc-icon"
            aria-label="Recolher"
            onClick={() => {
              setCollapsed(true);
              try {
                localStorage.setItem(COLLAPSE_KEY, '1');
              } catch {
                /* sem armazenamento: só não lembra */
              }
            }}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                d="M6 9.5 12 15l6-5.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </header>
        <div className="oc-bar" aria-hidden="true">
          {reach > 0 && (
            <>
              <i className="app" style={{ width: `${(batch.counts.appSent / reach) * 100}%` }} />
              <i
                className="wa"
                style={{ width: `${(batch.counts.whatsappSent / reach) * 100}%` }}
              />
            </>
          )}
        </div>
        <small className="oc-nums">
          {batch.counts.app > 0 && `${batch.counts.appSent} de ${batch.counts.app} pelo app`}
          {batch.counts.app > 0 && batch.counts.whatsapp > 0 && ' · '}
          {batch.counts.whatsapp > 0 &&
            `${batch.counts.whatsappSent} de ${batch.counts.whatsapp} no WhatsApp`}
        </small>
        {batch.counts.whatsapp > 0 && <WhatsAppTurn batch={batch} />}
      </aside>,
      document.body,
    );

  return createPortal(
    <>
      <button
        type="button"
        className={`oc oc-pill${ready ? ' ready' : ''}${batch.state === 'pausado' ? ' paused' : ''}${wide ? ' wide' : ''}`}
        style={
          dx
            ? { transform: `translateX(calc(-50% + ${dx}px))`, opacity: 1 - Math.abs(dx) / 200 }
            : undefined
        }
        aria-label={`Envio ${batch.title}`}
        onPointerDown={(event) => {
          drag.current = { x: event.clientX, id: event.pointerId };
        }}
        onPointerMove={(event) => {
          if (!drag.current) return;
          const moved = event.clientX - drag.current.x;
          if (Math.abs(moved) > 8) setDx(moved);
        }}
        onPointerUp={() => {
          const moved = dx;
          drag.current = null;
          setDx(0);
          if (Math.abs(moved) > 80) setDismissed(key);
        }}
        onPointerCancel={() => {
          drag.current = null;
          setDx(0);
        }}
        onClick={() => {
          if (dx) return;
          if (wide && collapsed) {
            setCollapsed(false);
            try {
              localStorage.removeItem(COLLAPSE_KEY);
            } catch {
              /* sem armazenamento */
            }
            return;
          }
          // Pronta: abre direto (o aberto antes conta como mandado; "Não mandei" fica na folha).
          if (ready) go();
          else {
            hapticTap();
            setSheet(true);
          }
        }}
      >
        {label}
        {more}
      </button>
      {sheet && (
        <div className="oc-scrim" onClick={() => setSheet(false)}>
          <section
            className="oc-sheet"
            role="dialog"
            aria-label={batch.title}
            onClick={(event) => event.stopPropagation()}
          >
            <span className="oc-grab" aria-hidden="true" />
            <header>
              <b>{batch.title}</b>
              <Link
                href={`/envios/${batch.id}`}
                className="oc-link"
                onClick={() => setSheet(false)}
              >
                Ver tudo ›
              </Link>
            </header>
            <small className="oc-nums">
              {sent} de {reach} saíram
            </small>
            {batch.counts.whatsapp > 0 ? (
              <WhatsAppTurn batch={batch} />
            ) : (
              <p className="wt-q">Os do app saem sozinhos. Pode fechar.</p>
            )}
            {error && (
              <p className="wt-err" role="alert">
                {error}
              </p>
            )}
          </section>
        </div>
      )}
    </>,
    document.body,
  );
}
